import { useCallback, useRef, useEffect, DependencyList } from 'react';

/**
 * Custom hook for debouncing function calls
 * Provides automatic cleanup and prevents memory leaks
 */
export const useDebounce = <T extends (...args: any[]) => any>(callback: T, delay: number): T => {
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);

    const debouncedCallback = useCallback(
        (...args: Parameters<T>) => {
            // Clear existing timeout
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }

            // Set new timeout
            timeoutRef.current = setTimeout(() => {
                callback(...args);
            }, delay);
        },
        [callback, delay]
    ) as T;

    // Cleanup function to clear timeout on unmount
    const cancel = useCallback(() => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
    }, []);

    // Attach cancel method to the debounced function
    (debouncedCallback as any).cancel = cancel;

    return debouncedCallback;
};

/**
 * Hook for debouncing effects with automatic cleanup
 * Usage: useDebounceEffect(() => { console.log('effect') }, [deps], 500)
 */
export const useDebounceEffect = (effect: () => void | (() => void), dependencies: DependencyList, delay: number) => {
    const debouncedEffect = useDebounce(effect, delay);

    // Use useEffect to trigger the debounced effect when dependencies change
    useEffect(() => {
        const cleanup = debouncedEffect();

        // Return cleanup function
        return () => {
            (debouncedEffect as any).cancel?.();
            if (typeof cleanup === 'function') {
                cleanup();
            }
        };
    }, dependencies);
};

export default useDebounce;
